import { useEffect, useState } from 'react';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  DollarSign, Search, CreditCard, RotateCcw, AlertCircle,
  Check, X, ChevronDown, Filter
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import ManualPaymentModal from '../components/ManualPaymentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  succeeded: 'bg-green-100 text-green-800',
  refunded: 'bg-red/10 text-red',
  pending: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-gray-100 text-gray-600'
};

const TYPE_LABELS = {
  registration: 'Registration',
  event: 'Event',
  fundraiser: 'Fundraiser',
  camp: 'Camp'
};

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [totalCollected, setTotalCollected] = useState(0);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Refund modal
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundReason, setRefundReason] = useState('requested_by_customer');
  const [refundProcessing, setRefundProcessing] = useState(false);
  const [refundError, setRefundError] = useState(null);

  // Record Manual Payment — registration picker + modal.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerRegs, setPickerRegs] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [manualTarget, setManualTarget] = useState(null); // registration to record against

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/registrations/list`, { withCredentials: true });
      setPickerRegs(res.data || []);
    } catch (err) {
      toast.error('Failed to load registrations');
    } finally {
      setPickerLoading(false);
    }
  };

  const filteredPickerRegs = pickerRegs.filter((r) => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return true;
    const p = r.player || {};
    const h = r.household || {};
    return (
      r.registration_id?.toLowerCase().includes(q) ||
      `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().includes(q) ||
      (h.primary_contact_email || '').toLowerCase().includes(q) ||
      (h.primary_contact_name || '').toLowerCase().includes(q)
    );
  }).slice(0, 40);

  const handleReverse = async (p) => {
    const reason = window.prompt('Reason for reversing this payment (required):');
    if (!reason || !reason.trim()) return;
    if (!p.reference_id) {
      toast.error('Cannot reverse: missing registration reference.');
      return;
    }
    try {
      await axios.post(
        `${API_URL}/api/admin/registrations/${p.reference_id}/payments/${p.payment_id}/reverse`,
        { reason: reason.trim() },
        { withCredentials: true }
      );
      toast.success('Payment reversed');
      fetchPayments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reversal failed');
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [typeFilter]);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = typeFilter ? `?payment_type=${typeFilter}` : '';
      const res = await axios.get(`${API_URL}/api/admin/payments${params}`, { withCredentials: true });
      setPayments(res.data.payments || []);
      setTotalCollected(res.data.total_collected || 0);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefundProcessing(true);
    setRefundError(null);

    try {
      await axios.post(
        `${API_URL}/api/admin/payments/${refundTarget.payment_id}/refund`,
        { reason: refundReason },
        { withCredentials: true }
      );
      setRefundTarget(null);
      fetchPayments();
    } catch (error) {
      setRefundError(error.response?.data?.detail || 'Refund failed');
    } finally {
      setRefundProcessing(false);
    }
  };

  const filteredPayments = payments.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.payer_name?.toLowerCase().includes(term) ||
      p.payer_email?.toLowerCase().includes(term) ||
      p.payment_id?.toLowerCase().includes(term) ||
      p.description?.toLowerCase().includes(term)
    );
  });

  const totalRefunded = payments
    .filter(p => p.status === 'refunded')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const formatDate = (d) => {
    if (!d) return 'N/A';
    try { return format(parseISO(d), 'MMM d, yyyy h:mm a'); } catch { return 'N/A'; }
  };

  return (
    <ParentLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-testid="admin-payments-page">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold mb-2">Finances</p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-navy uppercase tracking-tighter">Payments</h1>
            <p className="text-gray-500 text-sm mt-1">View and manage all payments collected</p>
          </div>
          <Button
            className="bg-navy hover:bg-navy/90 text-white"
            onClick={openPicker}
            data-testid="record-manual-payment-btn"
          >
            + Record Manual Payment
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Total Collected</p>
            <p className="text-2xl font-black text-green-600">${(totalCollected / 100).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Refunded</p>
            <p className="text-2xl font-black text-red">${(totalRefunded / 100).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Net Revenue</p>
            <p className="text-2xl font-black text-navy">${((totalCollected - totalRefunded) / 100).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Transactions</p>
            <p className="text-2xl font-black text-navy">{payments.length}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-navy focus:border-transparent"
              data-testid="payment-search"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2.5 border border-gray-300 rounded-lg bg-white"
            data-testid="payment-type-filter"
          >
            <option value="">All Types</option>
            <option value="registration">Registration</option>
            <option value="event">Event</option>
            <option value="fundraiser">Fundraiser</option>
            <option value="camp">Camp</option>
          </select>
        </div>

        {/* Payments Table */}
        {loading ? (
          <div className="space-y-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
                <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : filteredPayments.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <DollarSign className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No payments found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredPayments.map(p => (
              <div
                key={p.payment_id}
                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition"
                data-testid={`payment-row-${p.payment_id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      p.status === 'refunded' ? 'bg-red/10' : 'bg-green-100'
                    }`}>
                      {p.status === 'refunded' ? (
                        <RotateCcw className="w-5 h-5 text-red" />
                      ) : (
                        <CreditCard className="w-5 h-5 text-green-600" />
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-navy">{p.payer_name}</p>
                        {(() => {
                          // Finance F2 — payer identity provenance pill.
                          // Surfaces how the backend resolver matched
                          // this row to a person so admins can spot any
                          // rows that need data cleanup.
                          const v = p.payer_resolved_via;
                          if (!v) {
                            if (p.payer_name === 'Unknown') {
                              return (
                                <Badge
                                  className="bg-rose-100 text-rose-700 text-[10px]"
                                  data-testid={`payer-pill-${p.payment_id}`}
                                  title="No user, household, or registration link found for this payment."
                                >
                                  unresolved
                                </Badge>
                              );
                            }
                            return null;
                          }
                          const map = {
                            user_id: { label: 'verified', cls: 'bg-emerald-100 text-emerald-700',
                              title: 'Direct match: payment.user_id linked to a user record.' },
                            household_primary: { label: 'via household', cls: 'bg-amber-100 text-amber-700',
                              title: 'Resolved through household primary contact.' },
                            household_guardian: { label: 'via household', cls: 'bg-amber-100 text-amber-700',
                              title: 'Resolved through a household parent/guardian.' },
                            registration_household_primary: { label: 'via registration', cls: 'bg-sky-100 text-sky-700',
                              title: 'Resolved through the linked registration\'s household.' },
                            registration_household_guardian: { label: 'via registration', cls: 'bg-sky-100 text-sky-700',
                              title: 'Resolved through the linked registration\'s parent/guardian.' },
                          };
                          const meta = map[v] || { label: v, cls: 'bg-gray-100 text-gray-700', title: v };
                          return (
                            <Badge
                              className={`${meta.cls} text-[10px]`}
                              data-testid={`payer-pill-${p.payment_id}`}
                              title={meta.title}
                            >
                              {meta.label}
                            </Badge>
                          );
                        })()}
                        <Badge className={`${STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-600'} text-xs`}>
                          {p.status}
                        </Badge>
                        {p.payment_type && (
                          <Badge className="bg-navy/10 text-navy text-xs">
                            {TYPE_LABELS[p.payment_type] || p.payment_type}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate">
                        {p.payer_email} {p.card_brand && p.card_last4 ? `| ${p.card_brand} ****${p.card_last4}` : ''}
                      </p>
                      <p className="text-xs text-gray-400">{p.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="text-right">
                      <p className={`font-bold text-lg ${p.status === 'refunded' ? 'text-red line-through' : 'text-navy'}`}>
                        ${(p.amount / 100).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-400">{formatDate(p.created_at)}</p>
                    </div>
                    {p.status === 'succeeded' && p.payer_source === 'clover' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red border-red/30 hover:bg-red/5"
                        onClick={() => { setRefundTarget(p); setRefundError(null); }}
                        data-testid={`refund-btn-${p.payment_id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Refund
                      </Button>
                    )}
                    {p.status === 'succeeded' &&
                      (p.payer_source === 'manual' || p.payer_source === 'admin_verify') && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-orange-700 border-orange-300 hover:bg-orange-50"
                        onClick={() => handleReverse(p)}
                        data-testid={`reverse-btn-${p.payment_id}`}
                      >
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Reverse
                      </Button>
                    )}
                    {p.status === 'reversed' && (
                      <p className="text-xs text-orange-700">Reversed</p>
                    )}
                    {p.status === 'refunded' && p.refunded_at && (
                      <p className="text-xs text-red">Refunded {formatDate(p.refunded_at)}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Refund Confirmation Modal */}
        {refundTarget && (
          <>
            <div className="fixed inset-0 bg-black/40 z-50" onClick={() => setRefundTarget(null)} />
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden" data-testid="refund-modal">
                <div className="bg-red p-4 flex items-center justify-between">
                  <h3 className="text-white font-bold text-lg flex items-center gap-2">
                    <RotateCcw className="w-5 h-5" />
                    Confirm Refund
                  </h3>
                  <button onClick={() => setRefundTarget(null)} className="text-white/70 hover:text-white">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="p-5 space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-semibold text-navy">{refundTarget.payer_name}</p>
                    <p className="text-sm text-gray-500">{refundTarget.description}</p>
                    <p className="text-2xl font-black text-red mt-2">${(refundTarget.amount / 100).toFixed(2)}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Reason for refund</label>
                    <select
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      data-testid="refund-reason-select"
                    >
                      <option value="requested_by_customer">Requested by customer</option>
                      <option value="duplicate">Duplicate charge</option>
                      <option value="fraudulent">Fraudulent</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {refundError && (
                    <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-sm text-red-700">{refundError}</p>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setRefundTarget(null)}
                      disabled={refundProcessing}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-red hover:bg-red/90 text-white font-bold"
                      onClick={handleRefund}
                      disabled={refundProcessing}
                      data-testid="confirm-refund-btn"
                    >
                      {refundProcessing ? 'Processing...' : 'Refund Payment'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Registration Picker for manual payment */}
        {pickerOpen && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
               data-testid="registration-picker-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
              <div className="flex items-center justify-between border-b px-5 py-3">
                <h3 className="font-bold text-navy">Select a registration</h3>
                <button onClick={() => setPickerOpen(false)} className="text-gray-500 hover:text-gray-800">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-4 border-b">
                <input
                  autoFocus
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  placeholder="Search by player, parent, email, or registration ID…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  data-testid="picker-search"
                />
              </div>
              <div className="overflow-y-auto flex-1 divide-y">
                {pickerLoading ? (
                  <p className="p-4 text-sm text-gray-500">Loading…</p>
                ) : filteredPickerRegs.length === 0 ? (
                  <p className="p-4 text-sm text-gray-500">No matches.</p>
                ) : (
                  filteredPickerRegs.map((r) => {
                    const p = r.player || {};
                    return (
                      <button
                        key={r.registration_id}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center justify-between"
                        onClick={() => {
                          setManualTarget(r);
                          setPickerOpen(false);
                        }}
                        data-testid={`picker-row-${r.registration_id}`}
                      >
                        <div>
                          <div className="font-semibold text-navy text-sm">{p.first_name} {p.last_name}</div>
                          <div className="text-xs text-gray-500">
                            {r.registration_id} · Status: {r.payment_status || '—'}
                          </div>
                        </div>
                        <div className="text-right text-xs">
                          <div className="text-gray-500">Total</div>
                          <div className="font-mono text-navy">${Number(r.total_amount || 0).toFixed(2)}</div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        )}

        {/* Manual Payment modal (opened from picker) */}
        <ManualPaymentModal
          open={!!manualTarget}
          onClose={() => setManualTarget(null)}
          registration={manualTarget}
          onRecorded={() => {
            fetchPayments();
          }}
        />
      </div>
    </ParentLayout>
  );
}
